import type { JSX, ReactNode } from 'react';

import { cssClasses } from 'pkg/css/utils';

import * as css from './styles.css';

interface Props {
	rounded?: boolean;
	square?: boolean;
	small?: boolean;
	filled?: boolean;
	textarea?: boolean;
	transparent?: boolean;
	borderless?: boolean;
	hidden?: boolean;
	disabled?: boolean;
	className?: string;
	children?: ReactNode;
}
/**
 * InputBase component
 *
 * A flexible container for form input elements
 */
export default function InputBase({
	rounded,
	square,
	small,
	filled,
	textarea,
	transparent,
	borderless,
	hidden,
	disabled,
	className,
	children,
}: Props) {
	return (
		<div
			data-disabled={disabled}
			data-textarea={textarea}
			data-error-highlight
			className={cssClasses(
				css.inputBase,
				small && css.small,
				rounded && css.rounded,
				square && css.square,
				filled && css.filled,
				transparent && css.transparent,
				borderless && css.borderless,
				hidden && css.hidden,
				className && className
			)}>
			{children}
		</div>
	);
}

interface PrefixProps {
	inline?: boolean;
	testid?: string;
	children?: ReactNode;
	grayFill?: boolean;
}
/**
 * Prefix component
 *
 * Displays content at the start of an input field
 */
export function Prefix({
	inline,
	testid,
	children,
	grayFill,
}: PrefixProps): JSX.Element {
	return (
		<div
			className={cssClasses(
				css.prefix,
				inline && css.inline,
				grayFill && css.grayFill
			)}
			data-testid={testid}>
			{children}
		</div>
	);
}

interface SuffixProps {
	interactable?: boolean;
	inline?: boolean;
	testid?: string;
	children?: ReactNode;
	onClick?: () => void;
	className?: string;
}
/**
 * Suffix component
 *
 * Displays content at the end of an input field,
 */
export function Suffix({
	inline,
	interactable,
	testid,
	children,
	onClick,
	className,
}: SuffixProps): JSX.Element {
	return (
		<div
			className={cssClasses(
				css.suffix,
				inline && css.inline,
				interactable && css.interactable,
				!!onClick && css.interactable,
				className
			)}
			onClick={onClick}
			data-testid={testid}>
			{children}
		</div>
	);
}
